import '@fontsource/prompt/400-italic.css';
import '@fontsource/prompt/400.css';
import '@fontsource/prompt/500.css';
import '@fontsource/prompt/600.css';
import '@fontsource/prompt/700.css';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import '@mantine/tiptap/styles.css';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import dayjs from 'dayjs';
import 'dayjs/locale/ro';
import customFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { PageError, PageNotFound, PendingSpinner } from '@/core/components';

import './App.css';
import './config/axiosInitialize';
import './config/intl';
import { baseCssVariableResolver } from './config/theme/cssVariableResolver';
import './config/theme/styles.css';
import { baseTheme } from './config/theme/theme';
import { queryClient } from './queryClient';
// Import the generated route tree
import { routeTree } from './routeTree.gen';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(customFormat);
dayjs.extend(relativeTime);

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPendingComponent: PendingSpinner,
  defaultNotFoundComponent: PageNotFound,
  defaultErrorComponent: PageError,

  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return (
    <MantineProvider
      cssVariablesResolver={baseCssVariableResolver}
      theme={baseTheme}
    >
      <RouterProvider router={router} />
      <Notifications position="top-right" />
    </MantineProvider>
  );
}

export default App;
